// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import get from 'lodash/get'
import takeRight from 'lodash/takeRight'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import DateBar from '../../common/date/DateBar'
import withDate from '../../common/date/withDate'
import Loader from '../../common/loader/Loader'
import type { AuthStore } from '../auth/types'
import { i18n } from '../index'
import type { Station } from '../station/stationTypes'
import withActiveStation from '../station/withActiveStation'
import CustomerTargetGraph from './CustomerTargetGraph'
import CustomerTargetTable from './CustomerTargetTable'
import css from './customerTargetPage.css'
import { customerTargetPageQuery } from './customerTargetQueries'
import type { GroupedCustomerTargets, ProductGroup } from './customerTargetTypes'

type Props = {
  match: Record<string, any>
  location: Record<string, any>
  history: Record<string, any>
  activeStation: Station
  authStore: AuthStore
  isSavingCustomerTarget: boolean
  setIsSavingCustomerTarget: (value: boolean) => void
  saveCustomerTargetErrorMessage: string | undefined
  setSaveCustomerTargetErrorMessage: (value: string | undefined) => void
}

type State = {
  productGroup: ProductGroup
}

export const PRODUCT_GROUPS: ProductGroup[] = [
  'Buffet',
  'GroupBuffet',
  'GroupDining',
  'GroupPizzaTable',
  'GroupBuffetWorld',
  'Breakfast',
  'AbcPizzaTable',
  'ALaCarte',
  'ABCBurger',
  'ABCPizza',
  'Sweet',
  'Salty',
  'PikaCarte',
  'Arnolds',
  'AbcBuffetWorld',
]

@withDate({
  dateFormat: 'GGGG[W]WW',
  paramName: 'week',
})
@withActiveStation
@inject('authStore')
class CustomerTargetPage extends React.Component<Props, State> {
  state: State = {
    productGroup: PRODUCT_GROUPS[0],
  }

  onProductGroupChange = (productGroup: ProductGroup) => {
    this.setState({
      productGroup: productGroup,
    })
  }

  render() {
    const {
      date,
      dateFormat,
      onDateChange,
      activeStation,
      authStore,
      isSavingCustomerTarget,
      setIsSavingCustomerTarget,
      saveCustomerTargetErrorMessage,
      setSaveCustomerTargetErrorMessage,
    } = this.props
    const { productGroup } = this.state
    const current = moment(date)
    const yearAgo = current.clone().subtract(52, 'weeks')
    const fromDate = current.clone().startOf('week')
    const toDate = current.clone().endOf('week')
    const lastYearFromDate = yearAgo.clone().startOf('week')
    const lastYearToDate = yearAgo.clone().endOf('week')
    const salesFromDate = moment(fromDate).subtract(4, 'weeks').startOf('week')
    const salesToDate = moment(toDate).subtract(2, 'weeks').endOf('week')
    const userCanEdit = authStore.userHasPermission('edit_customerTargets')
    return (
      <Query
        query={customerTargetPageQuery}
        variables={{
          stationId: activeStation.id,
          fromDate: fromDate.format('YYYY-MM-DD'),
          lastYearFromDate: lastYearFromDate.format('YYYY-MM-DD'),
          lastYearToDate: lastYearToDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
          salesFromDate: salesFromDate.format('YYYY-MM-DD'),
          salesToDate: salesToDate.format('YYYY-MM-DD'),
        }}
        fetchPolicy='network-only'
      >
        {({ loading, error, data }) => {
          const thisYearDailySales = loading ? [] : get(data, 'thisYearDailySales', [])
          const lastYearDailySales = loading ? [] : get(data, 'lastYearDailySales', [])
          const thisYearSalesDetails = loading ? [] : get(data, 'thisYearSalesDetails', [])
          const lastYearSalesDetails = loading ? [] : get(data, 'lastYearSalesDetails', [])
          const dailySales = [...thisYearDailySales, ...lastYearDailySales]
          const salesDetails = [...thisYearSalesDetails, ...lastYearSalesDetails]
          const thisWeekSalesDetails = takeRight(thisYearSalesDetails, 7)
          const stationSettings = loading ? [] : get(data, 'stationSettings', [])
          const customerTargetsBuffet = loading ? [] : get(data, 'customerTargetsBuffet', [])
          const customerTargetsGroupBuffet = loading
            ? []
            : get(data, 'customerTargetsGroupBuffet', []).map((gb) => ({
                ...gb,
                productType: 'GroupBuffet',
              }))
          const customerTargetsSalty = loading ? [] : get(data, 'customerTargetsSalty', [])
          const customerTargetsSweet = loading ? [] : get(data, 'customerTargetsSweet', [])
          const customerTargetsALaCarte = loading ? [] : get(data, 'customerTargetsALaCarte', [])
          const customerTargetsGroupDining = loading
            ? []
            : get(data, 'customerTargetsGroupDining', []).map((gb) => ({
                ...gb,
                productType: 'GroupDining',
              }))
          const customerTargetsBurger = loading ? [] : get(data, 'customerTargetsBurger', [])
          const customerTargetsPizza = loading ? [] : get(data, 'customerTargetsPizza', [])
          const customerTargetsBreakfast = loading ? [] : get(data, 'customerTargetsBreakfast', [])
          const customerTargetsPizzatable = loading ? [] : get(data, 'customerTargetsPizzatable', [])
          const customerTargetsPikaCarte = loading ? [] : get(data, 'customerTargetsPikaCarte', [])
          const customerTargetsArnolds = loading ? [] : get(data, 'customerTargetsArnolds', [])
          const customerTargetsBuffetWorld = loading ? [] : get(data, 'customerTargetsBuffetWorld', [])
          const customerTargetsGroupPizzaTable = loading
            ? []
            : get(data, 'customerTargetsGroupPizzaTable', []).map((groupPizza) => ({
                ...groupPizza,
                productType: 'GroupPizzaTable',
              }))
          const customerTargetsGroupBuffetWorld = loading
            ? []
            : get(data, 'customerTargetsGroupBuffetWorld', []).map((groupBuffetWorld) => ({
                ...groupBuffetWorld,
                productType: 'GroupBuffetWorld',
              }))
          const customerTargets: GroupedCustomerTargets = {
            Buffet: customerTargetsBuffet,
            Salty: customerTargetsSalty,
            Sweet: customerTargetsSweet,
            GroupBuffet: customerTargetsGroupBuffet,
            ALaCarte: customerTargetsALaCarte,
            GroupDining: customerTargetsGroupDining,
            ABCBurger: customerTargetsBurger,
            ABCPizza: customerTargetsPizza,
            Breakfast: customerTargetsBreakfast,
            AbcPizzaTable: customerTargetsPizzatable,
            PikaCarte: customerTargetsPikaCarte,
            Arnolds: customerTargetsArnolds,
            GroupPizzaTable: customerTargetsGroupPizzaTable,
            AbcBuffetWorld: customerTargetsBuffetWorld,
            GroupBuffetWorld: customerTargetsGroupBuffetWorld,
          }
          return (
            <div className={css.component}>
              <h1 data-testid='customer-target-page-header'>{i18n.t('customerTarget.title')}</h1>
              <DateBar dateUnit='week' date={date} dateFormat={dateFormat} onDateChange={onDateChange} />
              <Loader loading={loading} error={error}>
                <div className={css.graph}>
                  <CustomerTargetGraph
                    week={date}
                    salesDetails={salesDetails}
                    productGroup={productGroup}
                    dailySales={dailySales}
                  />
                </div>
                <div className={css.table}>
                  <CustomerTargetTable
                    canEdit={userCanEdit}
                    isSavingCustomerTarget={isSavingCustomerTarget}
                    setIsSavingCustomerTarget={setIsSavingCustomerTarget}
                    saveCustomerTargetErrorMessage={saveCustomerTargetErrorMessage}
                    setSaveCustomerTargetErrorMessage={setSaveCustomerTargetErrorMessage}
                    week={date}
                    salesDetails={thisWeekSalesDetails}
                    productGroup={productGroup}
                    customerTargets={customerTargets}
                    dailySales={dailySales}
                    onProductGroupChange={this.onProductGroupChange}
                    stationSettings={stationSettings}
                  />
                </div>
              </Loader>
            </div>
          )
        }}
      </Query>
    )
  }
}

export default withRouter(CustomerTargetPage)
